import { ColDef, ColumnState, ValueFormatterParams, CellStyle } from '@ag-grid-community/core';
import { isArray, pick } from 'lodash';
import { EM_DASH } from './AGTable.constants';

export const defaultValueFormatter = <TData>(params: ValueFormatterParams<TData>): string =>
  params.value || params.value === 0 ? params.value : EM_DASH;

export const defaultColumnDefinition: ColDef = {
  resizable: true,
  sortable: true,
  editable: false,
  minWidth: 100,
  valueFormatter: defaultValueFormatter,
  cellStyle: (params): CellStyle | null => {
    if (typeof params.value === 'number') {
      return { textAlign: 'right' };
    }
    return null;
  },
};

type PartialColumnState = Pick<ColumnState, 'colId' | 'pinned' | 'width'>;
const partialColumnStateKeys: (keyof PartialColumnState)[] = ['colId', 'pinned', 'width'];
export const getColumnState = (columnPersistenceKey: string): PartialColumnState[] | null => {
  const columnStateString = localStorage.getItem(columnPersistenceKey);
  if (!columnStateString) {
    return null;
  }
  try {
    return JSON.parse(columnStateString);
  } catch {
    return null;
  }
};

export const agTableGetColumns = <TData>(
  columnPersistenceKey: string,
  initialColumns: ColDef<TData>[],
  allAvailableColumns?: ColDef<TData>[],
  ensureInitialColumnsExist = true
): ColDef<TData>[] => {
  const columnState = getColumnState(columnPersistenceKey);
  if (!columnState || !isArray(columnState) || columnState.length === 0) {
    return initialColumns;
  }
  // Update and filter out already saved column definitions in case there would be differences
  const columnDefs: ColDef<TData>[] = columnState
    .map<ColDef<TData> | null>((colState) => {
      const columns = allAvailableColumns || initialColumns;
      const colDef = columns.find((colDef) => colDef.colId === colState.colId);
      if (!colDef) {
        return null;
      }
      return {
        ...colDef,
        ...colState,
      };
    })
    .filter((colDef): colDef is ColDef<TData> => Boolean(colDef));

  if (ensureInitialColumnsExist) {
    // Check if all default columns are present in saved state map
    for (const initialColdDef of initialColumns) {
      if (!columnDefs.find((colDef) => colDef.colId === initialColdDef.colId)) {
        columnDefs.push(initialColdDef);
      }
    }
  }

  return columnDefs;
};

export const persistColumnState = (columnPersistenceKey: string, columnState: ColumnState[]): void => {
  const partialColumnState: PartialColumnState[] = columnState.map((state) => pick(state, partialColumnStateKeys));
  localStorage.setItem(columnPersistenceKey, JSON.stringify(partialColumnState));
};

export const agTableValueComparator = (
  value1: string | number | undefined | null,
  value2: string | number | undefined | null
): number => {
  if (value1 === null || value1 === undefined) {
    return -1;
  }
  if (value2 === null || value2 === undefined) {
    return 1;
  }
  if (value1 === value2) {
    return 0;
  }
  return value1 > value2 ? 1 : -1;
};
